// ──────────────────────────── Feuille De Temps Status ────────────────────────────
// Maps the `statut: string | null` from the backend entity (FeuilleDeTempsStatut enum)
// to display-ready status configuration (canonical shape, plan §2.5.2).
//
// IMPORTANT: `code` must match the backend FeuilleDeTempsStatut enum values exactly
// (EN_ATTENTE, VALIDE, REJETE) — these are sent as query params to the API.
// `token` is an L2.5 semantic token (lib/config/statusTokens.ts) — StatusBadge
// resolves token → --status-<token> / --status-<token>-bg pairs.

import type { StatusToken } from "@lib/config/statusTokens";
import {
  CalendarClock,
  CheckCircle,
  Clock,
  type LucideIcon,
  Plane,
  Sparkles,
  Sun,
  Timer,
  UserX,
  XCircle,
} from "lucide-react";

export interface StatusConfig {
  readonly code: string;
  readonly label: string;
  readonly token: StatusToken;
  readonly icon?: LucideIcon;
}

export const STATUT_FEUILLE = {
  VALIDEE: {
    code: "VALIDE",
    label: "Validée",
    token: "completed",
    icon: CheckCircle,
  },
  REJETEE: {
    code: "REJETE",
    label: "Rejetée",
    token: "rejected",
    icon: XCircle,
  },
  EN_ATTENTE: {
    code: "EN_ATTENTE",
    label: "En attente",
    token: "pending",
    icon: Clock,
  },
} as const satisfies Record<string, StatusConfig>;

export type FeuilleStatus = keyof typeof STATUT_FEUILLE;

/**
 * Maps the backend statut field to a FeuilleStatus.
 * - `'VALIDE'`   → VALIDEE
 * - `'REJETE'`   → REJETEE
 * - `'EN_ATTENTE'` or null/undefined → EN_ATTENTE
 */
export function resolveStatutFeuille(statut: string | null | undefined): FeuilleStatus {
  if (statut === "VALIDE") return "VALIDEE";
  if (statut === "REJETE") return "REJETEE";
  return "EN_ATTENTE";
}

// ──────────────────────────── Jour Status ────────────────────────────
// Maps the `statut` of a day in the week summary (backend `JourStatut` enum)
// to display-ready configuration.
//
// `code` MUST match the backend `JourStatut` enum values exactly. Tokens are
// reused from the shared L2.5 scale rather than added: the distinction that
// matters here is worked / not-yet / penalty, and the existing pairs already
// carry it.

export interface JourStatusConfig {
  readonly code: string;
  readonly label: string;
  readonly token: StatusToken;
  readonly icon?: LucideIcon;
  /** Whether the day still owes a verdict (open day: no delta to show). */
  readonly pending?: boolean;
  /** Whether the day ended badly enough to deserve attention. */
  readonly penalised?: boolean;
}

export const JOUR_STATUT = {
  TRAVAILLE: {
    code: "TRAVAILLE",
    label: "Travaillé",
    token: "completed",
    icon: CheckCircle,
  },
  RETARD: {
    code: "RETARD",
    label: "Retard",
    token: "overdue",
    icon: Clock,
    penalised: true,
  },
  HEURES_SUP: {
    code: "HEURES_SUP",
    label: "Heures sup",
    token: "pending",
    icon: Timer,
  },
  ABSENT: {
    code: "ABSENT",
    label: "Absent",
    token: "rejected",
    icon: UserX,
    penalised: true,
  },
  A_VENIR: {
    code: "A_VENIR",
    label: "À venir",
    token: "draft",
    icon: CalendarClock,
    pending: true,
  },
  EN_COURS: {
    code: "EN_COURS",
    label: "En cours",
    token: "in-progress",
    icon: Clock,
    pending: true,
  },
  CONGE: {
    code: "CONGE",
    label: "Congé",
    token: "controlled",
    icon: Plane,
  },
  FERIE: {
    code: "FERIE",
    label: "Férié",
    token: "cancelled",
    icon: Sparkles,
  },
  REPOS: {
    code: "REPOS",
    label: "Repos",
    token: "archived",
    icon: Sun,
  },
} as const satisfies Record<string, JourStatusConfig>;

export type JourStatus = keyof typeof JOUR_STATUT;

/** Neutral fallback for an unknown code, so a new backend value never blanks the card. */
export const JOUR_STATUT_FALLBACK: JourStatusConfig = {
  code: "INCONNU",
  label: "—",
  token: "draft",
};
